<template>
    <!-- Mobile app bar: back (not on the main tabs), logo, page title, me (my player and my team) -->
    <header class="app-header">
        <div class="app-header__bar">
            <button v-if="!isRootPage" type="button" class="app-header__icon-btn" aria-label="Πίσω" @click="goBack">
                <v-icon>mdi-chevron-left</v-icon>
            </button>
            <!-- the BIG logo is always next to the divider -->
            <router-link :to="{ name: 'News' }" class="app-header__logo">
                <img :src="require('@/assets/main_logo.png')" alt="BIG">
            </router-link>

            <span class="app-header__divider" />
            <h1 class="app-header__title">{{ title }}</h1>

            <!-- me: my player with the badge of my team (like the top bar of the website); the menu leads to the player, the team and the requests -->
            <v-menu v-if="myTeam || myPlayer" offset-y bottom left transition="slide-y-transition" content-class="top-bar__menu">
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="app-header__me" aria-label="Ο παίκτης και η ομάδα μου" v-bind="attrs" v-on="on">
                        <span class="app-header__photo" :class="{ 'app-header__photo--logo': !myPlayer }">
                            <img v-if="myPlayer && myPlayer.image" :src="`${assetsUrl}/${myPlayer.image}`" alt="">
                            <img v-else-if="!myPlayer && myTeam.logo" :src="`${assetsUrl}/${myTeam.logo}`" alt="">
                            <v-icon v-else color="orange darken-2">{{ myPlayer ? 'mdi-account' : 'mdi-account-group' }}</v-icon>
                        </span>
                        <span v-if="myPlayer && myTeam" class="app-header__badge">
                            <img v-if="myTeam.logo" :src="`${assetsUrl}/${myTeam.logo}`" alt="">
                            <v-icon v-else x-small color="orange">mdi-account-group</v-icon>
                        </span>
                    </button>
                </template>
                <v-list dense class="top-bar__menu-list">
                    <div class="menu-greeting">
                        <div v-if="myPlayer">{{ myPlayer.name }} {{ myPlayer.surname }}</div>
                        <div v-if="myTeam" class="app-header__menu-team">{{ myTeam.name }}</div>
                    </div>
                    <v-list-item v-if="myPlayer" :to="{ name: 'Player', params: { id: myPlayer.id } }">
                        <v-list-item-icon class="mr-3"><v-icon small color="orange darken-2">mdi-account-outline</v-icon></v-list-item-icon>
                        <v-list-item-title class="menu-item__title">Ο παίκτης μου</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="myTeam" :to="{ name: 'Team', params: { id: myTeam.id } }">
                        <v-list-item-icon class="mr-3"><v-icon small color="orange darken-2">mdi-shield-star-outline</v-icon></v-list-item-icon>
                        <v-list-item-title class="menu-item__title">Η ομάδα μου</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="myTeam" :to="{ name: 'MatchChanges' }">
                        <v-list-item-icon class="mr-3"><v-icon small color="orange darken-2">mdi-calendar-sync-outline</v-icon></v-list-item-icon>
                        <v-list-item-title class="menu-item__title">Τα αιτήματά μου</v-list-item-title>
                    </v-list-item>
                    <v-list-item :to="{ name: 'Profile' }">
                        <v-list-item-icon class="mr-3"><v-icon small color="grey darken-1">mdi-cog-outline</v-icon></v-list-item-icon>
                        <v-list-item-title class="menu-item__title">Ρυθμίσεις</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <router-link v-else :to="{ name: 'Profile' }" class="app-header__me" aria-label="Διάλεξε παίκτη και ομάδα">
                <span class="app-header__photo">
                    <v-icon color="orange darken-2">mdi-account-plus-outline</v-icon>
                </span>
            </router-link>
        </div>
    </header>
</template>

<script>
    // The sections of the bottom tab bar: no back button there, like the root screens of an app
    const ROOT_PAGES = ['News', 'Standings', 'Schedule', 'Teams']

    export default {
        props: {
            title: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            isRootPage() {
                return ROOT_PAGES.includes(this.$route.name)
            },
            myTeam() {
                const team = this.$store.getters.getTeam
                return team && team.id ? team : null
            },
            myPlayer() {
                const player = this.$store.getters.getPlayer
                return player && player.id ? player : null
            }
        },
        methods: {
            goBack() {
                // opened from a link (no history inside the app): go to the home screen instead of leaving
                if (window.history.length > 1) {
                    this.$router.go(-1)
                } else {
                    this.$router.push({ name: 'News' })
                }
            }
        }
    }
</script>

<style scoped>
.app-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.08);
}

.app-header__bar {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 56px;
    padding: 0 8px;
}

.app-header__icon-btn {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 40px;
    margin-right: -4px;
    border-radius: 50%;
    -webkit-tap-highlight-color: transparent;
}

.app-header__icon-btn:active {
    background: #f4f4f6;
}

.app-header__icon-btn .v-icon {
    color: #2b2b2e !important;
    font-size: 30px;
}

/* the same size as the player / team circles on the right */
.app-header__logo {
    display: inline-flex;
    text-decoration: none;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
}

.app-header__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.app-header__divider {
    flex: 0 0 auto;
    width: 3px;
    height: 26px;
    border-radius: 3px;
    background: linear-gradient(180deg, #ef4444 0%, #ff7133 60%, orange 100%);
}

.app-header__title {
    flex: 1 1 auto;
    text-align: left;
    min-width: 0;
    overflow: hidden;

    /* custom-orange gradient text */
    background-image: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* narrow phones: the back button and the logo take room from the title */
@media (max-width: 420px) {
    .app-header__title {
        font-size: 1.12rem;
    }
}

.app-header__me {
    position: relative;
    display: inline-flex;
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    margin-right: 4px;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
}

/* no square focus ring on a tap; the keyboard still gets it */
.app-header__me:focus:not(:focus-visible) {
    outline: none;
}

.app-header__photo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #f4f4f6;
    overflow: hidden;
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(255, 113, 51, 0.6);
}

/* player photo: fill the circle from the top, faces are in the upper part */
.app-header__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

/* no player: the logo of the team */
.app-header__photo--logo img {
    padding: 2px;
    object-fit: contain;
}

/* the badge of my team on the corner of my player */
.app-header__badge {
    position: absolute;
    right: -5px;
    bottom: -4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 0 2px #fff, 0 1px 3px rgba(0, 0, 0, 0.2);
    overflow: hidden;
}

.app-header__badge img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.app-header__menu-team {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 600;
}
</style>
